import { useCallback, useEffect } from 'react'
import { styled, Typography } from '@mui/material'
import { Message } from '@weblite/ts-action/message'
import { t } from '$i18n'
import {
  MessagePreviewDescriptorContainer,
  MessagePreviewInfoContainer,
  MessagePreviewInfoRow,
  MessagePreviewMessageTypeIconMap,
  MessagePreviewThumbnail,
} from '$common/messsage-preview'
import { toLocale } from '$func/text'
import PinNavigator from '$comp/_app/components/chat/components/pin/components/components/pin-navigator'
import { useClickOnPin } from '$comp/_app/components/chat/components/pin/components/react-hooks'
import { MessageDescriptor } from './components'

const Root = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  height: 38,
  overflow: 'hidden',

  position: 'relative',

  boxSizing: 'border-box',
  marginRight: '4px',
  gap: '4px',
}))

interface Props {
  messages: Message[]
}

let onScrollHandlerExport: (direction: string) => unknown
let getLastExport: () => unknown

export function PinContent({ messages }: Props) {
  const firstMessage = messages[0]

  const MessageTypeIcon =
    messages.length === 1 && MessagePreviewMessageTypeIconMap[firstMessage.type]

  const hasTypeIcon = !!MessageTypeIcon

  const {
    onClick,
    currentIndex,
    getLast,
    setIsAnimating,
    isAnimating,
  } = useClickOnPin(messages)

  const onScrollHandler = useCallback(
    (direction: string) => {
      if (isAnimating) {
        return
      }
      if (direction === 'down') getLast()
    },
    [isAnimating, getLast],
  )

  useEffect(() => {
    onScrollHandlerExport = onScrollHandler
    getLastExport = getLast
  }, [getLast, onScrollHandler])

  return (
    <Root
      style={{ cursor: 'pointer' }}
      onClick={() => {
        onClick()
        setIsAnimating(true)
      }}
    >
      <>
        <PinNavigator
          index={messages.length - currentIndex - 1}
          count={messages.length}
        />
        <MessagePreviewThumbnail message={messages[currentIndex]} height={38} />

        <MessagePreviewInfoContainer>
          <MessagePreviewInfoRow>
            <Typography
              variant="subtitle4"
              color="secondary"
              sx={{ display: 'block', lineHeight: '14px' }}
              noWrap
            >
              {t('chat.pinned')}
            </Typography>
            {currentIndex !== messages.length - 1 && (
              <Typography
                variant="messageMedium"
                color="secondary"
                sx={{
                  display: 'block',
                  lineHeight: '14px',
                  marginRight: '4px',
                }}
                noWrap
              >
                {toLocale(currentIndex + 1)}#
              </Typography>
            )}
          </MessagePreviewInfoRow>

          <MessagePreviewInfoRow>
            {hasTypeIcon && (
              <MessageTypeIcon
                sx={{ width: '20px', height: '18px', color: 'gray' }}
              />
            )}
            <MessagePreviewDescriptorContainer hasMarginToIcon={hasTypeIcon}>
              <MessageDescriptor message={messages[currentIndex]} />
            </MessagePreviewDescriptorContainer>
          </MessagePreviewInfoRow>
        </MessagePreviewInfoContainer>
      </>
    </Root>
  )
}

PinContent.defaultProps = {}

export const pinContentApi = {
  onScroll(direction: string) {
    onScrollHandlerExport?.(direction)
  },
  getLast() {
    getLastExport?.()
  },
}
